
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, addDoc, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { ArrowLeft, Check, ChevronDown, Clock, HelpCircle, X } from 'lucide-react';
import { createSupportRequestDocument } from '../../../../dbSchema/supportRequestSchema';

const BRAND_COLORS = {
    primary: '#1a1a1a',
    accent: '#C9A961',
    light: '#F5F5F0',
    lightGray: '#E8E8E3',
};

const SupportScreen = ({ user, role }) => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [supportRequests, setSupportRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(true);

    useEffect(() => {
        if (user) {
            fetchRecentJobs();

            const q = query(
                collection(db, 'support_requests'),
                where('userId', '==', user.uid),
                orderBy('createdAt', 'desc')
            );

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const requests = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setSupportRequests(requests);
                setLoadingRequests(false);
            }, (error) => {
                console.error("Error fetching support requests:", error);
                setLoadingRequests(false);
            });

            return () => unsubscribe();
        }
    }, [user, role]);

    const fetchRecentJobs = async () => {
        setLoadingJobs(true);
        try {
            let jobsRef = collection(db, 'jobs');
            let q;

            if (role === 'employer') {
                q = query(jobsRef, where('employerId', '==', user.uid), orderBy('createdAt', 'desc'), limit(20));
            } else {
                q = query(jobsRef, where('assignedProfessionalIds', 'array-contains', user.uid), orderBy('createdAt', 'desc'), limit(20));
            }

            const snapshot = await getDocs(q);
            const fetchedJobs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setJobs(fetchedJobs);
        } catch (error) {
            console.error("Error fetching jobs for support:", error);
        } finally {
            setLoadingJobs(false);
        }
    };

    const handleSubmit = async () => {
        if (!message.trim()) {
            alert("Please describe your issue");
            return;
        }

        setSubmitting(true);
        try {
            let userName = user.displayName;
            if (!userName) {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    userName = userDoc.data()?.displayName;
                }
            }

            const supportData = createSupportRequestDocument({
                userId: user.uid,
                userType: role,
                relatedId: selectedJob ? selectedJob.id : '',
                relatedType: selectedJob ? 'job' : '',
                msg: message,
                userPhoneNumber: user.phoneNumber || '',
                userName: userName || 'Unknown User',
            });

            await addDoc(collection(db, 'support_requests'), supportData);
            setShowSuccess(true);
            setMessage('');
            setSelectedJob(null);
        } catch (error) {
            console.error("Error submitting support request:", error);
            alert("Failed to submit request. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (showSuccess) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
                    <div className="max-w-3xl mx-auto flex items-center">
                        <h1 className="text-xl font-medium">Contact Us</h1>
                    </div>
                </div>
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="bg-white p-8 rounded-2xl shadow-sm items-center w-full max-w-md text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="w-8 h-8 text-green-500" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Request Received</h2>
                        <p className="text-base text-gray-600 mb-6">
                            We will contact you <span className="font-bold" style={{ color: BRAND_COLORS.accent }}>within next 10 minutes</span>.
                        </p>
                        <button
                            onClick={() => setShowSuccess(false)}
                            className="bg-black text-white font-medium py-3 px-8 rounded-full hover:bg-gray-800 transition-colors"
                            style={{ backgroundColor: BRAND_COLORS.primary }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {/* Header */}
            <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
                <div className="max-w-3xl mx-auto flex items-center">
                    <button onClick={() => navigate(`/${role}/home`)} className="mr-4 p-2 hover:bg-gray-100 rounded-full">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-medium">Contact Us</h1>
                </div>
            </div>

            <div className="max-w-3xl mx-auto p-4 mt-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">How can we help?</h2>
                <p className="text-gray-500 mb-8">Select a job related to your issue (optional)</p>

                {/* Job Selector */}
                <div className="relative mb-6">
                    <button
                        onClick={() => setPickerVisible(!pickerVisible)}
                        className="w-full bg-white p-4 rounded-xl border border-gray-200 flex justify-between items-center shadow-sm hover:border-gray-300 transition-colors text-left"
                    >
                        <div className="flex-1">
                            <p className="text-xs text-gray-400 mb-1 uppercase font-medium">Related Job</p>
                            <p className={`text-base ${selectedJob ? 'text-gray-900' : 'text-gray-400 italic'} truncate`}>
                                {selectedJob ? selectedJob.title : 'Select a job...'}
                            </p>
                        </div>
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                    </button>

                    {/* Dropdown */}
                    {pickerVisible && (
                        <div className="absolute z-20 w-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 max-h-60 overflow-y-auto">
                            <div className="p-2 sticky top-0 bg-white border-b border-gray-100 flex justify-between items-center">
                                <span className="font-medium pl-2">Select Job</span>
                                <button onClick={() => setPickerVisible(false)} className="p-1 hover:bg-gray-100 rounded-full">
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            </div>
                            {loadingJobs ? (
                                <div className="p-4 text-center text-gray-400">Loading jobs...</div>
                            ) : jobs.length > 0 ? (
                                jobs.map(job => (
                                    <button
                                        key={job.id}
                                        onClick={() => {
                                            setSelectedJob(job);
                                            setPickerVisible(false);
                                        }}
                                        className="w-full p-4 border-b border-gray-50 hover:bg-gray-50 text-left transition-colors last:border-0"
                                    >
                                        <p className="font-medium mb-1" style={{ color: BRAND_COLORS.accent }}>{job.title}</p>
                                        <p className="text-xs text-gray-500">{job.date} • {job.status}</p>
                                    </button>
                                ))
                            ) : (
                                <div className="p-4 text-center text-gray-400">No recent jobs found</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Message Input */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-8">
                    <p className="text-xs mb-2 uppercase font-bold tracking-wide" style={{ color: BRAND_COLORS.accent }}>Issue Description</p>
                    <textarea
                        className="w-full text-base text-gray-900 min-h-[120px] resize-y outline-none placeholder-gray-400"
                        placeholder="Tell us what happened..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                </div>

                {/* Submit Button */}
                <button
                    onClick={handleSubmit}
                    disabled={!message.trim() || submitting}
                    className="w-full bg-black text-white font-bold text-lg p-4 rounded-xl shadow-md disabled:opacity-70 hover:opacity-90 transition-opacity flex justify-center items-center"
                    style={{ backgroundColor: BRAND_COLORS.primary }}
                >
                    {submitting ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    ) : (
                        'Ask for Help'
                    )}
                </button>

                <div className="mt-6 flex justify-center items-center mb-12">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <p className="text-gray-500 text-xs ml-2">We will contact you within next 10 minutes</p>
                </div>

                {/* History Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Your Recent Tickets</h3>
                    {loadingRequests ? (
                        <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: BRAND_COLORS.accent }}></div>
                        </div>
                    ) : supportRequests.length > 0 ? (
                        <div className="space-y-3">
                            {supportRequests.map(item => (
                                <div key={item.id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="font-bold text-gray-900 flex-1 mr-2 truncate">
                                            {item.relatedType === 'job' ? `Job Issue` : 'Support Request'}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                            {item.status === 'resolved' ? 'Resolved' : 'Pending'}
                                        </span>
                                    </div>
                                    <p className="text-gray-600 text-sm mb-2 line-clamp-2">{item.msg}</p>
                                    <p className="text-xs text-gray-400">
                                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-400 text-center italic py-4">No recent tickets</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportScreen;
